import React, { useState } from 'react';
import { useBankAccounts, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function BankAccounts() {
  const { data: accounts, isLoading } = useBankAccounts();
  const { invalidateBankAccounts } = useInvalidate();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', bank_name: '', account_number: '', iban: '', currency: 'EUR', balance: '', is_cash: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/bank-accounts/${editing.id}`, form);
        toast.success('U përditësua!');
      } else {
        await api.post('/bank-accounts', form);
        toast.success('U shtua!');
      }
      setShowModal(false);
      setEditing(null);
      setForm({ name: '', bank_name: '', account_number: '', iban: '', currency: 'EUR', balance: '', is_cash: false });
      invalidateBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, bank_name: item.bank_name || '', account_number: item.account_number || '', iban: item.iban || '', currency: item.currency, balance: item.balance, is_cash: item.is_cash });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt?')) return;
    try {
      await api.delete(`/bank-accounts/${id}`);
      toast.success('U fshi!');
      invalidateBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const getCurrencySymbol = (c) => c === 'EUR' ? '€' : c === 'USD' ? '$' : 'L';

  // Calculate totals
  const totals = accounts?.reduce((acc, a) => {
    acc[a.currency] = (acc[a.currency] || 0) + parseFloat(a.balance);
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Llogaritë Bankare</h1>
        <button onClick={() => { setEditing(null); setForm({ name: '', bank_name: '', account_number: '', iban: '', currency: 'EUR', balance: '', is_cash: false }); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" /> Shto Llogari
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(totals).map(([currency, total]) => (
          <div key={currency} className="card bg-gradient-to-r from-primary-500 to-primary-600 text-white">
            <p className="text-primary-100">Total {currency}</p>
            <p className="text-3xl font-bold">{getCurrencySymbol(currency)}{total.toFixed(2)}</p>
          </div>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts?.map((item) => (
              <div key={item.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{item.name}</p>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(item)} className="p-1 text-gray-500 hover:text-primary-600"><PencilIcon className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-1 text-gray-500 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-gray-500">{item.bank_name || (item.is_cash ? 'Cash' : '-')}</p>
                <p className={`text-xl font-bold mt-2 ${parseFloat(item.balance) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {getCurrencySymbol(item.currency)}{parseFloat(item.balance).toFixed(2)}
                </p>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${item.is_cash ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                  {item.is_cash ? 'Cash' : 'Bankë'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Ndrysho' : 'Shto'} Llogari</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Emri *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Emri Bankës</label><input type="text" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Nr. Llogarisë</label><input type="text" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label><input type="text" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Monedha</label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input-field">
                  <option value="EUR">EUR (€)</option>
                  <option value="ALL">ALL (Lek)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Balanca</label><input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="input-field" /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_cash" checked={form.is_cash} onChange={(e) => setForm({ ...form, is_cash: e.target.checked })} className="rounded" />
                <label htmlFor="is_cash" className="text-sm text-gray-700">Arka (Cash)</label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
